import React from 'react';
import styled, { css } from 'styled-components';
import { currentUserIDState, currentUserWalletState } from '@atom/setting/user';
import Theme from '@styles/Theme';
import { IPhotoCard } from '@typings/photocard/photocard.interface';
import { useRecoilValue } from 'recoil';
import WalletPrice, { formatPrice } from '@pages/Store/component/WalletPrice';
import { useDocument } from '@nandorojo/swr-firestore';
import { CollectionPath } from '@typings/collections.enum';
import { useNavigate } from 'react-router-dom';
import CircleAvatarLink from '@components/dataDisplay/Avatar/CircleAvatarLink';
import { ICampItem } from '@typings/photocard/camp.interface';
import useToggle from '@hooks/useToggle';
import { IhomepageSetting } from '@typings/setting/homepageSetting.interface';
import {
  CHAR_PANEL_TYPE,
  CHAR_PANEL_TYPE_VALUE,
} from '@typings/menu/charPanelType';
import useWindowWidth from '@hooks/useWindowWidth';
import { layoutTypeState } from '@atom/setting/homepageSet';
import {
  LAYOUT_TYPE,
  LAYOUT_TYPE_VALUE,
  CUSTOM_LAYOUT_TYPE_VALUE,
} from '@typings/setting/layoutType';
import ActionButtons from './ActionButtons';
import {
  mainPhotocardDataState,
  mainCharacterState,
  IMainCharacter,
} from '@atom/setting/photocardAutoSetState';
import QuickRegisterRepresentativeBtn from '@pages/UserSetting/MyCharacter/QuickRegisterRepresentativeBtn';
import {
  homeSettingState,
  defaultCurrencyState,
} from '@atom/setting/homeSettingState';
import { IthemeColorSet } from '@typings/setting/themeColor.interface';
import {
  charPanelLayoutOverrideState,
  charPanelForcedHiddenState,
  charPanelBgImageOverrideState,
} from '@atom/setting/charPanelOverride';
import { showNavState } from '@atom/setting/navigation';
import { useExplorationPoint } from '@hooks/useExplorationPoint';

const styleMap = {
  default: css`
    background: var(--menuBackgroundColor);
    box-shadow: ${Theme.shadow.card};
    backdrop-filter: blur(8px);
  `,
  img: css``,
};

const dirMap = {
  [CHAR_PANEL_TYPE.BOTTOMLEFT]: ({ isVisible }) => css`
    bottom: 12px;
    left: 0;
    border-top-right-radius: 50px;
    border-bottom-right-radius: 50px;

    --panel-offset: calc(-100% + 34px);
    transform: ${isVisible
      ? 'translateX(0)'
      : 'translateX(var(--panel-offset))'};
  `,
  [CHAR_PANEL_TYPE.BOTTOMRIGHT]: ({ isVisible }) => css`
    bottom: 12px;
    right: 0;
    border-top-left-radius: 50px;
    border-bottom-left-radius: 50px;

    --panel-offset: calc(100% - 34px);
    transform: ${isVisible
      ? 'translateX(0)'
      : 'translateX(var(--panel-offset))'};
  `,
  [CHAR_PANEL_TYPE.TOPLEFT]: ({ isVisible }) => css`
    top: 12px;
    left: 0;
    border-top-right-radius: 50px;
    border-bottom-right-radius: 50px;

    --panel-offset: calc(-100% + 34px);
    transform: ${isVisible
      ? 'translateX(0)'
      : 'translateX(var(--panel-offset))'};
  `,
  [CHAR_PANEL_TYPE.TOPRIGHT]: ({ isVisible }) => css`
    top: 12px;
    right: 0;
    border-top-left-radius: 50px;
    border-bottom-left-radius: 50px;
    --panel-offset: calc(100% - 34px);
    transform: ${isVisible
      ? 'translateX(0)'
      : 'translateX(var(--panel-offset))'};
  `,
};

const marginMap = {
  [CHAR_PANEL_TYPE.BOTTOMLEFT]: css`
    margin-bottom: 114px;
  `,
  [CHAR_PANEL_TYPE.BOTTOMRIGHT]: css`
    margin-bottom: 114px;
  `,
  [CHAR_PANEL_TYPE.TOPLEFT]: css`
    margin-top: 114px;
  `,
  [CHAR_PANEL_TYPE.TOPRIGHT]: css`
    margin-top: 114px;
  `,
};

export const PanelContainer = styled.div<{
  layoutType: LAYOUT_TYPE_VALUE;
  isVisible: boolean;
  bgImgURL?: string;
  dir: CHAR_PANEL_TYPE_VALUE;
  marginDir?: boolean;
}>`
  position: fixed;
  z-index: 3;
  width: 100%;
  max-width: 440px;
  max-height: calc(100dvh - 200px);
  height: fit-content;
  padding: 16px 24px;

  display: flex;
  gap: 8px;
  align-items: flex-start;
  transition: transform 0.3s ease-in-out;
  ${({ bgImgURL }) => (bgImgURL ? styleMap.img : styleMap.default)}
  ${({ dir, isVisible }) => dirMap[dir]({ isVisible })}
  ${({ dir, marginDir }) => marginDir && marginMap[dir]}

  @media (max-width: 480px) {
    width: 100%;
    max-width: 100%;
  }
`;

const PanelBgImg = styled.img<{
  dir: CHAR_PANEL_TYPE_VALUE;
  isVisible: boolean;
}>`
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;
  pointer-events: none;

  @media (max-width: 480px) {
    display: none;
  }
`;

const TitleSec = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
`;
const InfoSec = styled.div`
  width: 100%;
  z-index: 1;

  .title {
    font-size: 15px;
    font-weight: 700;
    color: var(--menuTextColor);
  }
  .camp {
    font-size: 12px;
    color: var(--subTextColor);
  }

  @media (max-width: 480px) {
    .title {
      font-size: 12px;
    }
    .camp {
      font-size: 10px;
    }
  }
`;

const Empty = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  color: var(--subTextColor);
  gap: 12px;

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const PanelHorizontal = styled.div<{
  panelAlign?: 'left' | 'center' | 'right';
  $hasBgImg?: boolean;
}>`
  position: fixed;
  width: fit-content;
  z-index: 1;
  top: var(--gameui-panel-top-offset, 0px);
  left: var(--gameui-panel-offset, 0px);
  right: 0;
  background: transparent;
  display: flex;
  justify-content: ${({ panelAlign }) =>
    panelAlign === 'center'
      ? 'center'
      : panelAlign === 'right'
      ? 'flex-end'
      : 'flex-start'};
  align-items: flex-start;
  pointer-events: none;
  transition: left 0.2s ease;

  @media (max-width: 480px) {
    padding: 8px 8px 0 8px;
  }
`;

/* ── GAMEUI HUD ── */

const GameHudBar = styled.div<{ $bgImgURL?: string }>`
  display: flex;
  align-items: stretch;
  gap: 0;
  background: var(--backgroundColor);
  color: var(--textColor);
  backdrop-filter: blur(var(--panelBlur, 8px));
  border: 1px solid var(--lineColor);
  border-radius: var(--panelBorderRadius, var(--cardBorderRadius));
  overflow: hidden;
  pointer-events: auto;
  /* padding: ${({ $bgImgURL }) => ($bgImgURL ? '0' : '12px 16px')}; */
  padding: 16px 24px;

  ${({ $bgImgURL }) =>
    $bgImgURL &&
    css`
      background: transparent;
      border-color: transparent;
      backdrop-filter: none;
    `}

  @media (max-width: 480px) {
    padding: 8px 12px;
  }
`;

const AvatarCol = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  cursor: var(--hoverCursorURL), pointer;
  flex-shrink: 0;
  transition: background 0.15s;
`;

type Props = {
  direction?: CHAR_PANEL_TYPE_VALUE;
  customLayoutType?: CUSTOM_LAYOUT_TYPE_VALUE;
  style?: React.CSSProperties;
  forceShow?: boolean;
  /** ActionButtons 아이콘·텍스트 색상. 기본값: var(--textColor) */
  iconColor?: string;
};
function CharacterPanel({
  customLayoutType,
  direction,
  style,
  forceShow,
  iconColor = 'var(--textColor)',
}: Props) {
  const homeSet = useRecoilValue(homeSettingState);
  const navigate = useNavigate();
  const layoutType = useRecoilValue(layoutTypeState);
  const layoutOverride = useRecoilValue(charPanelLayoutOverrideState);
  const isForcedHidden = useRecoilValue(charPanelForcedHiddenState);
  const bgImageOverride = useRecoilValue(charPanelBgImageOverrideState);
  const isNavShown = useRecoilValue(showNavState);

  // 우선순위: atom override → prop customLayoutType → 전역 layoutType
  const type = layoutOverride || customLayoutType || layoutType;

  const currentUid = useRecoilValue(currentUserIDState);
  const [isVisible, toggleVisible] = useToggle(true); //토글용

  // 📍 통합된 대표 캐릭터 (포토카드 우선, 없으면 폼)
  const mainCharacter = useRecoilValue<IMainCharacter | null>(
    mainCharacterState,
  );
  // 📍 포토카드 데이터 (ActionButtons에서 필요)
  const photocardData = useRecoilValue(mainPhotocardDataState);

  // GAMEUI HUD용 재화/탐색 포인트
  const currentUserWallet = useRecoilValue(currentUserWalletState);
  const defaultCurrency = useRecoilValue(defaultCurrencyState);
  const {
    explorationPoint,
    maxPoints,
    isActive: isExplorationActive,
  } = useExplorationPoint();
  const explorationPct =
    maxPoints > 0 ? (explorationPoint / maxPoints) * 100 : 0;

  const dir =
    direction || homeSet?.cummuMode?.panelDir || CHAR_PANEL_TYPE.TOPRIGHT;
  const panelAlign = homeSet?.cummuMode?.panelAlign || 'left';

  const windowwidth = useWindowWidth();
  const isMobile = windowwidth < 480;

  // 진영 데이터 (캐릭터에 camp가 있을 때만 로드)
  const { data: campData } = useDocument<ICampItem>(
    mainCharacter?.camp ? `${CollectionPath.CAMP}/${mainCharacter.camp}` : null,
    { listen: true },
  );

  // 📍 대표 캐릭터 링크 이동 (포토카드 우선, 없으면 폼 결과 페이지)
  const handleAvatarClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!mainCharacter) return;

      if (mainCharacter.type === 'photocard' && mainCharacter.id) {
        navigate(`/${CollectionPath.PHOTOCARD}/${mainCharacter.id}`);
      } else if (
        mainCharacter.type === 'form' &&
        mainCharacter.form?.formPath &&
        mainCharacter.id
      ) {
        navigate(`/${mainCharacter.form.formPath}-result/${mainCharacter.id}`);
      }
    },
    [mainCharacter, navigate],
  );

  // 패널에 표시할 정보 (통합된 atom에서 가져옴)
  const panelThumbnail = mainCharacter?.thumbnail || '';
  const panelTitle = mainCharacter?.title || '';
  const panelSubtitle =
    mainCharacter?.type === 'photocard'
      ? campData?.title
      : mainCharacter?.form?.formConfigTitle;

  // 강제 숨김 (cummuMode.isActive 여부와 무관)
  if (isForcedHidden) return null;

  // override 또는 forceShow가 있으면 모든 guard 무시
  const isForced = !!layoutOverride || forceShow;

  // showNav(false)가 호출된 페이지(PhotocardDetail 등)에서는 숨김
  // 단, layoutOverride가 명시적으로 설정된 페이지(WorldMap 등)는 예외
  if (!isForced && isNavShown === false) return null;

  if (
    !isForced &&
    ((isMobile && layoutType !== LAYOUT_TYPE.GAMEUI) ||
      layoutType === LAYOUT_TYPE.UNSET)
  )
    return null;

  // cummuMode 디자인 설정에서 저장한 테마 색상을 CSS 변수로 변환
  // layoutOverride가 활성화된 경우(WorldMap 등), 페이지가 자체 테마를 전역 CSS 변수에 이미
  // 적용하고 있으므로 패널 테마를 덮어쓰지 않고 전역 변수를 그대로 사용한다.
  const panelThemeSet = (homeSet?.cummuMode as any)?.themeSet as
    | IthemeColorSet
    | undefined;
  const panelBorderRadiusValue = panelThemeSet?.panelBorderRadius;
  const panelThemeVars: React.CSSProperties =
    !layoutOverride && panelThemeSet?.isCustomTheme
      ? ({
          ...(panelThemeSet.backgroundColor && {
            '--menuBackgroundColor': panelThemeSet.backgroundColor,
            '--backgroundColor': panelThemeSet.backgroundColor,
          }),
          ...(panelThemeSet.textColor && {
            '--menuTextColor': panelThemeSet.textColor,
            '--textColor': panelThemeSet.textColor,
          }),
          ...(panelThemeSet.subTextColor && {
            '--subTextColor': panelThemeSet.subTextColor,
          }),
          ...(panelThemeSet.cardBackgroundColor && {
            '--cardBackgroundColor': panelThemeSet.cardBackgroundColor,
          }),
          ...(panelThemeSet.lineColor && {
            '--lineColor': panelThemeSet.lineColor,
          }),
          ...(panelThemeSet.primaryColor && {
            '--primaryColor': panelThemeSet.primaryColor,
          }),
          ...(panelThemeSet.subColor && {
            '--subColor': panelThemeSet.subColor,
          }),
          ...(panelThemeSet.buttonBackgroundColor && {
            '--buttonBackgroundColor': panelThemeSet.buttonBackgroundColor,
          }),
          ...(panelThemeSet.buttonTextColor && {
            '--buttonTextColor': panelThemeSet.buttonTextColor,
          }),
          ...(panelBorderRadiusValue && {
            '--panelBorderRadius': panelBorderRadiusValue,
          }),
          ...(panelThemeSet.cardBlur && {
            '--panelBlur': panelThemeSet.cardBlur,
          }),
        } as React.CSSProperties)
      : panelBorderRadiusValue
      ? ({
          '--panelBorderRadius': panelBorderRadiusValue,
        } as React.CSSProperties)
      : {};

  const panelBgImgURL =
    bgImageOverride !== null
      ? bgImageOverride || undefined
      : homeSet?.cummuMode?.isPanelBgImgActive ??
        !!homeSet?.cummuMode?.panelBgImgURL
      ? homeSet?.cummuMode?.panelBgImgURL
      : undefined;

  const renderPanel = () => {
    if (type === LAYOUT_TYPE.GAMEUI) {
      return (
        <PanelHorizontal
          style={{ ...panelThemeVars, ...style }}
          panelAlign={panelAlign}
        >
          {panelBgImgURL && (
            <PanelBgImg
              src={panelBgImgURL}
              alt=""
              dir={dir}
              isVisible={isVisible}
            />
          )}
          <GameHudBar $bgImgURL={panelBgImgURL}>
            {/* ── 아바타 (세로 전체) ── */}
            <AvatarCol onClick={handleAvatarClick}>
              <CircleAvatarLink
                uid={currentUid}
                photoURL={panelThumbnail}
                onClick={handleAvatarClick}
                disableNavigation
              />
            </AvatarCol>

            {/* ── 재화 + 탐색포인트 ── */}
            <InfoSec>
              <TitleSec>
                <div className="title">{panelTitle}</div>
                <div className="camp">{panelSubtitle}</div>
              </TitleSec>
              <WalletPrice uid={currentUid || ''} showMyWalet showPoint />

              {/* ── 액션 버튼 ── */}
              {photocardData && (
                <ActionButtons
                  homeSet={homeSet as IhomepageSetting}
                  data={photocardData as IPhotoCard}
                  isVisible={isVisible}
                  direction={dir}
                  iconColor={iconColor}
                />
              )}
            </InfoSec>
          </GameHudBar>
        </PanelHorizontal>
      );
    }

    return (
      <PanelContainer
        layoutType={layoutType}
        onClick={layoutType === LAYOUT_TYPE.GAMEUI ? () => {} : toggleVisible}
        isVisible={isVisible}
        bgImgURL={panelBgImgURL}
        dir={dir}
        style={{ ...panelThemeVars, ...style }}
      >
        {!mainCharacter || !currentUid ? (
          <Empty>
            배정된 캐릭터가 없습니다.
            <QuickRegisterRepresentativeBtn size="sm" />
          </Empty>
        ) : (
          <>
            {panelBgImgURL && (
              <PanelBgImg
                onClick={toggleVisible}
                src={panelBgImgURL}
                alt=""
                dir={dir}
                isVisible={isVisible}
              />
            )}
            <CircleAvatarLink
              uid={currentUid}
              photoURL={panelThumbnail}
              onClick={handleAvatarClick}
              disableNavigation
            />
            <InfoSec
              onClick={
                layoutType === LAYOUT_TYPE.GAMEUI ? () => {} : toggleVisible
              }
            >
              <TitleSec>
                <div className="title">{panelTitle}</div>
                <div className="camp">{panelSubtitle}</div>
              </TitleSec>
              <WalletPrice uid={currentUid || ''} showMyWalet showPoint />
              {photocardData && (
                <ActionButtons
                  homeSet={homeSet as IhomepageSetting}
                  data={photocardData as IPhotoCard}
                  isVisible={isVisible}
                  direction={dir}
                  iconColor={iconColor}
                />
              )}
            </InfoSec>
          </>
        )}
      </PanelContainer>
    );
  };
  return renderPanel();
}

export default CharacterPanel;
